import { service } from '@ember/service';

import type { RecordData, Service, Slack } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { getFormattedTime } from 'webapp/utils/number';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Report from 'webapp/models-custom/report';
// import type UserRecord from 'webapp/models/user';
import type ReportDeliveryRecord from 'webapp/models/report-delivery';

import { Severity } from 'webapp/services/logger';

export default class ReportDelivery extends BufferedModel<
	ReportDeliveryRecord,
	RecordData.ReportDelivery.Serialized
> {
	@service('logger') declare loggerService: Service.Logger;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get reportId(): number | null {
		return this.getBuffered('reportId');
	}

	get timestamp(): number {
		return this.getBuffered('timestamp');
	}

	get title(): string {
		return this.getBuffered('title');
	}

	get image(): string {
		return this.getBuffered('image');
	}

	get slideCount(): number {
		return this.getBuffered('slideCount');
	}

	get reportUrl(): string {
		return this.getBuffered('reportUrl');
	}

	get size(): number {
		return this.model.size;
	}

	// get approvedBy(): number[] {
	// 	return this.getBuffered('approvedBy');
	// }

	get recipients(): string[] {
		return this.getBuffered('recipients');
	}

	get slackChannels(): Slack.Channels {
		return this.getBuffered('slackChannels');
	}

	// ------- other props ------

	get report(): Report | null {
		let report;
		if (this.reportId) {
			report = this.mmReportsService.findModel(String(this.reportId));
			if (!report) {
				this.loggerService.logError(
					'Missing report for report delivery',
					{
						reportDeliveryId: this.id,
						reportId: this.reportId,
					},
					Severity.WARNING,
				);
			}
		}
		return report || null;
	}

	get timestampText(): string {
		return getFormattedTime(
			this.timestamp,
			this.currentUserService.model?.timezone,
			'MMM D, YYYY [at] hh:mm A',
		);
	}

	get mappedRecipients() {
		return this.mmUsersService.getMappedRecipients(
			this.recipients,
			[],
			this.slackChannels,
			false,
		);
	}

	get sizeFormatted(): string {
		const size = this.size;
		if (size) {
			const mbytes = Math.round((size * 100) / 1024 / 1024) / 100;
			return `${mbytes} MB`;
		} else {
			return '';
		}
	}

	// get approvedByUser(): UserRecord | null {
	// 	let user;
	// 	if (this.approvedBy) {
	// 		user = this.mmUsersService.findRecord(String(this.approvedBy));
	// 	}
	// 	return user || null;
	// }

	static override modelName = ModelName.REPORT_DELIVERY; // store record type
}
